import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { KeyboardAvoidingView, Platform } from 'react-native';
import {
    ControlledTextInput,
    FootnoteRegular,
    Form,
    FruitfulCustomIcon,
    StyledH5Input,
    USE_FORM_MODE_DEFAULTS,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import {
    SPACE_NAME_MAX_LENGTH,
    type UpdateBankAccountInfo,
} from '../../../../types/BankAccount';
import CreateEditButton from './CreateEditButton';

const PLACEHOLDER_TEXT = [
    'Emergency fund',
    'Walkin around money',
    'Wedding reception',
    'House down payment',
    'Llama farm',
    'New car',
    'Home renovation',
    'Creed tickets',
];

const spaceNameSchema = z.object({
    nickname: z
        .string()
        .min(2, 'Please enter a nickname')
        .max(
            SPACE_NAME_MAX_LENGTH,
            `Maximum ${SPACE_NAME_MAX_LENGTH} characters`,
        ),
});

type SpaceNameSchema = z.infer<typeof spaceNameSchema>;

interface NameProps {
    onPressNext: (updates?: Partial<UpdateBankAccountInfo>) => void;
    accountInfo: UpdateBankAccountInfo;
}

const Name = ({ onPressNext, accountInfo }: NameProps) => {
    const [placeholderIndex, setPlaceholderIndex] = useState(0);

    useEffect(() => {
        // Timer to cycle through placeholder text for account nickname every few seconds
        const timer = setInterval(() => {
            setPlaceholderIndex(
                (prevIndex) => (prevIndex + 1) % PLACEHOLDER_TEXT.length,
            );
        }, 1250);

        // Clear the interval timer when the component unmounts
        return () => {
            clearInterval(timer);
        };
    }, []);

    const {
        register,
        watch,
        control,
        handleSubmit,
        setFocus,
        formState: { errors, isValid },
    } = useForm<SpaceNameSchema>({
        ...USE_FORM_MODE_DEFAULTS,
        resolver: zodResolver(spaceNameSchema),
        defaultValues: {
            nickname: accountInfo?.nickname ?? '',
        },
    });

    useEffect(() => {
        setTimeout(() => {
            setFocus('nickname');
        }, 500);
    }, []);

    const nickname = watch('nickname');

    const onSubmit: SubmitHandler<SpaceNameSchema> = ({ nickname }) => {
        onPressNext({ nickname });
    };

    return (
        <YStack height="100%" justifyContent="space-between">
            <View width="100%" maxWidth={450} alignSelf="center">
                <Form width="100%" onSubmit={handleSubmit(onSubmit)}>
                    <View alignSelf="center" marginBottom={24}>
                        <FruitfulCustomIcon
                            icon={accountInfo?.icon}
                            color={accountInfo?.color}
                            size="xl"
                            showShadow
                        />
                    </View>
                    {/* TODO: For space creation, update the placeholder to "My Space" or "Our Space" determined whether the account was created as joint or solo */}
                    <ControlledTextInput
                        Input={StyledH5Input}
                        register={register}
                        name="nickname"
                        enterKeyHint="done"
                        control={control}
                        errors={errors}
                        value={nickname}
                        placeholder={PLACEHOLDER_TEXT[placeholderIndex]}
                        textAlign="center"
                        borderWidth={0}
                    />
                </Form>
                <FootnoteRegular alignSelf="center" marginTop={4}>
                    {nickname.length}/{SPACE_NAME_MAX_LENGTH}
                </FootnoteRegular>
            </View>
            <KeyboardAvoidingView
                behavior={Platform.OS === 'ios' ? 'position' : undefined}
                keyboardVerticalOffset={220}
            >
                <CreateEditButton
                    disabled={!isValid}
                    onPress={handleSubmit(onSubmit)}
                />
            </KeyboardAvoidingView>
        </YStack>
    );
};

export default Name;
